// OrganisationModulesSection — the employer "Moduler" list.
//
// Step 1 of the authoring-surface unification (#223): shares the same shell
// (ContentListShell) + row (ContentEntityRow) + action cluster (Redigera /
// Förhandsgranska / Radera) as the Rekrytering list. The whole row opens the
// course; Preview shows the student view ("Visa som student").
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Trash2, Pencil, Eye, Clock, Calendar, FileText, Plus, GraduationCap, X, Briefcase, Send, Archive, RotateCcw } from 'lucide-react';
import { onboardingApi, type ModuleSourceBrief, type ModuleUsage } from '@/lib/api/onboardingApi';
import { moduleApi } from '@/lib/api/moduleApi';
import { sourcesApi } from '@/lib/api/sourcesApi';
import { Module, type CourseStatus } from '@/lib/models/module';
import type { Source } from '@/lib/models/source';
import {
    ActionButton,
    EmptyState,
    ContentEntityRow,
    ContentListShell,
    ConfirmDialog,
    DeleteConfirmDialog,
    IslandSlideOver,
    StatusBadge,
    type RowAction,
} from '@/components/custom-ui';
import ModuleViewer from '@/components/modules/ModuleViewer';
import { CourseAvatar } from '@/components/modules/CourseAvatar';
import { LinkAvatar } from '@/components/icons/LinkAvatar';
import { useLinkLauncher } from '@/contexts/LinkLauncherContext';
import { SourceViewerModal } from '@/components/sources';

interface OrganisationModulesSectionProps {
    refreshKey?: number;
    onCreate?: () => void;
}

const fmtDate = (iso?: string): string => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('sv-SE');
};

// UsageBanner — "WHERE is this course used?" — surfaced prominently on each
// course row (#40). A course can be used in onboarding programs and/or as a
// job-application prerequisite. Each is a clickable chip → jump to that program
// or annons. Unused courses get a clear, quieter note.
const UsageBanner: React.FC<{
    usage?: ModuleUsage;
    onOpenNeed: (id: string) => void;
    onOpenPrograms: () => void;
}> = ({ usage, onOpenNeed, onOpenPrograms }) => {
    const programs = usage?.programs ?? [];
    const needs = usage?.needs ?? [];

    if (programs.length === 0 && needs.length === 0) {
        return <p className="mt-1.5 text-xs text-gray-400">Används inte ännu</p>;
    }

    return (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Används i</span>
            {programs.map((p) => (
                <button
                    key={p.id}
                    onClick={(e) => { e.stopPropagation(); onOpenPrograms(); }}
                    title={`Onboarding-program: ${p.name}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#1A4D4F]/8 text-[#1A4D4F] hover:bg-[#1A4D4F]/15 transition-colors max-w-[220px]"
                >
                    <GraduationCap className="w-3 h-3 flex-shrink-0 text-[#1A4D4F]" />
                    <span className="truncate">{p.name}</span>
                </button>
            ))}
            {needs.map((n) => (
                <button
                    key={n.id}
                    onClick={(e) => { e.stopPropagation(); onOpenNeed(n.id); }}
                    title={`Förkunskapskrav i annonsen ${n.role}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#1A4D4F]/8 text-[#1A4D4F] hover:bg-[#1A4D4F]/15 transition-colors max-w-[220px]"
                >
                    <Briefcase className="w-3 h-3 flex-shrink-0 text-amber-700" />
                    <span className="truncate">{n.role}</span>
                </button>
            ))}
        </div>
    );
};

// step_type → short Swedish label, in display order.
const STEP_ORDER = ['info', 'quiz', 'case', 'checkpoint', 'task', 'confirmation', 'file_review'];
const STEP_LABEL: Record<string, string> = {
    info: 'info',
    quiz: 'quiz',
    case: 'case',
    checkpoint: 'kontroll',
    task: 'uppgift',
    confirmation: 'bekräftelse',
    file_review: 'filgranskning',
};

const OrganisationModulesSection: React.FC<OrganisationModulesSectionProps> = ({ refreshKey, onCreate }) => {
    const navigate = useNavigate();
    const { stageLink } = useLinkLauncher();
    const [modules, setModules] = useState<Module[]>([]);
    const [sourcesByModule, setSourcesByModule] = useState<Record<string, ModuleSourceBrief[]>>({});
    const [usageByModule, setUsageByModule] = useState<Record<string, ModuleUsage>>({});
    const [stepCounts, setStepCounts] = useState<Record<string, Record<string, number>>>({});
    const [viewerSource, setViewerSource] = useState<Source | null>(null);
    const [previewModule, setPreviewModule] = useState<Module | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState<Module | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    // #75 course lifecycle transition, gated behind a confirm dialog (mirrors the
    // needs state-change flow).
    const [pendingStatus, setPendingStatus] = useState<{
        module: Module; status: CourseStatus; title: string; description: string; confirmLabel: string;
    } | null>(null);
    const [statusBusy, setStatusBusy] = useState(false);

    const confirmStatusChange = async () => {
        if (!pendingStatus) return;
        setStatusBusy(true);
        try {
            await moduleApi.updateModuleStatus(pendingStatus.module.id, pendingStatus.status);
            setPendingStatus(null);
            await loadModules();
        } catch (err) {
            console.error('Failed to change course status:', err);
            alert('Kunde inte ändra modulens status.');
        } finally {
            setStatusBusy(false);
        }
    };

    // Lifecycle row-actions available for a course given its current status.
    const lifecycleActions = (module: Module): RowAction[] => {
        const status = module.status ?? 'draft';
        if (status === 'draft') {
            return [{
                icon: Send, label: 'Publicera',
                onClick: () => setPendingStatus({
                    module, status: 'published',
                    title: 'Publicera modulen?',
                    description: 'Modulen blir tillgänglig att lägga till i program och som förkunskapskrav vid rekrytering. Först då kan kandidater se den.',
                    confirmLabel: 'Publicera',
                }),
            }];
        }
        if (status === 'published') {
            return [{
                icon: Archive, label: 'Arkivera',
                onClick: () => setPendingStatus({
                    module, status: 'archived',
                    title: 'Arkivera modulen?',
                    description: 'Modulen döljs för nya kandidater och kan inte längre läggas till i program eller annonser. Kandidater som redan påbörjat den påverkas inte. Du kan återpublicera senare.',
                    confirmLabel: 'Arkivera',
                }),
            }];
        }
        // archived
        return [{
            icon: RotateCcw, label: 'Återpublicera',
            onClick: () => setPendingStatus({
                module, status: 'published',
                title: 'Återpublicera modulen?',
                description: 'Modulen blir tillgänglig igen och kan läggas till i program och annonser.',
                confirmLabel: 'Återpublicera',
            }),
        }];
    };

    const openSource = async (id: string) => {
        try {
            setViewerSource(await sourcesApi.getSource(id));
        } catch { /* ignore — source may have been removed */ }
    };

    const stepChipsFor = (moduleId: string): { label: string; count: number }[] => {
        const counts = stepCounts[moduleId];
        if (!counts) return [];
        return STEP_ORDER
            .filter((t) => counts[t] > 0)
            .map((t) => ({ label: STEP_LABEL[t] ?? t, count: counts[t] }));
    };

    useEffect(() => {
        loadModules();
    }, [refreshKey]);

    const loadModules = async () => {
        setIsLoading(true);
        try {
            const result = await onboardingApi.getOnboardingModules();
            setModules(result.modules || []);
            setSourcesByModule(result.sources_by_module || {});
            setUsageByModule(result.usage_by_module || {});
            setStepCounts(result.step_counts || {});
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        try {
            await onboardingApi.deleteOnboardingModule(deleteTarget.id);
            setDeleteTarget(null);
            await loadModules();
        } catch (err) {
            console.error('Failed to delete module:', err);
            alert('Kunde inte radera modulen.');
        } finally {
            setIsDeleting(false);
        }
    };

    const emptyState = (
        <EmptyState
            icon={BookOpen}
            title="Inga moduler ännu"
            description="Skapa din första modul — den kan återanvändas i program och som krav vid rekrytering."
            action={onCreate ? { label: 'Skapa Modul', onClick: onCreate, icon: Plus } : undefined}
        />
    );

    const action = onCreate ? (
        <ActionButton icon={Plus} onClick={onCreate}>
            Skapa Modul
        </ActionButton>
    ) : undefined;

    return (
        <>
            <ContentListShell
                action={action}
                isLoading={isLoading}
                loadingMessage="Laddar moduler..."
                isEmpty={!isLoading && modules.length === 0}
                emptyState={emptyState}
            >
                {modules.map((module) => {
                    const actions: RowAction[] = [
                        // Row-click → read-detail page; pencil → the editor directly.
                        { icon: Pencil, label: 'Redigera', onClick: () => navigate(`/dashboard/onboarding/modules/${module.id}`) },
                        { icon: Eye, label: 'Förhandsgranska', onClick: () => setPreviewModule(module) },
                        // #75 lifecycle (Publicera / Arkivera / Återpublicera).
                        ...lifecycleActions(module),
                        {
                            icon: LinkAvatar,
                            label: 'Chatta med Link',
                            // Same staged (unsent) @-tagged prompt as the detail page.
                            onClick: () => stageLink({
                                reference: { type: 'module', id: module.id },
                                label: module.module_name,
                                prompt: 'Hjälp mig att jobba vidare med den här modulen.',
                            }),
                        },
                        { icon: Trash2, label: 'Radera', tone: 'danger', onClick: () => setDeleteTarget(module) },
                    ];
                    return (
                        <ContentEntityRow
                            key={module.id}
                            icon={BookOpen}
                            leading={
                                <CourseAvatar
                                    organisationName={module.organisation_name}
                                    organisationLogoUrl={module.organisation_logo_url}
                                />
                            }
                            title={module.module_name}
                            badge={<StatusBadge status={module.status ?? 'draft'} />}
                            onOpen={() => navigate(`/dashboard/courses/${module.id}`)}
                            actions={actions}
                        >
                            {module.module_description && (
                                <p className="text-sm text-gray-500 truncate">{module.module_description}</p>
                            )}
                            {/* Step breakdown + duration */}
                            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                                {module.estimated_duration && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                                        <Clock className="w-3 h-3" /> {module.estimated_duration}
                                    </span>
                                )}
                                {stepChipsFor(module.id).map((c) => (
                                    <span key={c.label} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                                        {c.count}&nbsp;{c.label}
                                    </span>
                                ))}
                            </div>
                            {/* Sources — clickable chips */}
                            {(sourcesByModule[module.id]?.length ?? 0) > 0 && (
                                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                                    <span className="text-xs text-gray-400">Källor:</span>
                                    {sourcesByModule[module.id].map((s) => (
                                        <button
                                            key={s.id}
                                            onClick={(e) => { e.stopPropagation(); openSource(s.id); }}
                                            title={`Öppna ${s.name}`}
                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-[#A4C3B2]/25 text-[#1A4D4F] hover:bg-[#A4C3B2]/45 transition-colors max-w-[240px]"
                                        >
                                            <FileText className="w-3 h-3 flex-shrink-0" />
                                            <span className="truncate">{s.name}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {/* Dates */}
                            <div className="flex flex-wrap items-center gap-x-3 text-xs text-gray-400 mt-1.5">
                                {module.updated_at && (
                                    <span className="flex items-center gap-1">
                                        <Calendar className="w-3 h-3" /> Uppdaterad {fmtDate(module.updated_at)}
                                    </span>
                                )}
                                {module.created_at && <span>Skapad {fmtDate(module.created_at)}</span>}
                            </div>
                            {/* Where is this course used? (#40) — clickable, at the bottom */}
                            <UsageBanner
                                usage={usageByModule[module.id]}
                                onOpenNeed={(id) => navigate(`/dashboard/needs/${id}`)}
                                onOpenPrograms={() => navigate('/dashboard/onboarding')}
                            />
                        </ContentEntityRow>
                    );
                })}
            </ContentListShell>

            {/* Preview — the student view, as an overlay (parallels the needs
                preview + the editor's "Visa som student"). Quick peek without
                leaving the list. */}
            {previewModule && (
                <IslandSlideOver onClose={() => setPreviewModule(null)} fullScreen zClass="z-50">
                    <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100 flex-shrink-0">
                        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                            <GraduationCap className="w-4 h-4 text-[#1A4D4F]" /> Studentvy — {previewModule.module_name}
                        </div>
                        <button onClick={() => setPreviewModule(null)} className="p-2 hover:bg-gray-100 rounded-lg" aria-label="Stäng">
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-6 bg-gray-50/40">
                        <div className="max-w-3xl mx-auto">
                            <ModuleViewer moduleId={previewModule.id} preview onClose={() => setPreviewModule(null)} />
                        </div>
                    </div>
                </IslandSlideOver>
            )}

            {viewerSource && (
                <SourceViewerModal source={viewerSource} onClose={() => setViewerSource(null)} />
            )}

            <DeleteConfirmDialog
                open={!!deleteTarget}
                onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
                title="Radera modul"
                entityLabel="modulen"
                entityName={deleteTarget?.module_name || ''}
                onConfirm={handleDelete}
                isDeleting={isDeleting}
            />

            <ConfirmDialog
                open={pendingStatus !== null}
                onOpenChange={(open) => { if (!open) setPendingStatus(null); }}
                title={pendingStatus?.title || ''}
                description={pendingStatus?.description}
                confirmLabel={pendingStatus?.confirmLabel}
                onConfirm={confirmStatusChange}
                isBusy={statusBusy}
            />
        </>
    );
};

export default OrganisationModulesSection;
